import { useEffect, useRef, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  superadminApi, releaseImageUrl,
  type ReleaseItemKind, type ReleaseNoteDto, type ReleaseNoteSaveRequest,
} from '@/api/superadmin'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { ArrowDown, ArrowLeft, ArrowUp, ExternalLink, Eye, EyeOff, ImagePlus, Plus, Trash2, X } from 'lucide-react'
import { errorMessage } from './ReleaseNotesPage'

const KINDS: ReleaseItemKind[] = ['New', 'Improved', 'Fixed']
const KIND_PILL: Record<ReleaseItemKind, string> = {
  New: 'bg-indigo-50 text-indigo-700 border-indigo-200',
  Improved: 'bg-blue-50 text-blue-700 border-blue-200',
  Fixed: 'bg-amber-50 text-amber-700 border-amber-200',
}
const MAX_ITEMS = 30

type ItemForm = {
  key: string
  id: string | null
  kind: ReleaseItemKind
  heading: string
  body: string
  imageId: string | null
  imageWidth: number | null
  imageHeight: number | null
  imageAlt: string
}

type Form = {
  slug: string
  title: string
  summary: string
  metaDescription: string
  date: string
  heroImageId: string | null
  heroImageWidth: number | null
  heroImageHeight: number | null
  sourceVersions: string
  items: ItemForm[]
}

const newKey = () => crypto.randomUUID()

function emptyItem(): ItemForm {
  return { key: newKey(), id: null, kind: 'New', heading: '', body: '', imageId: null, imageWidth: null, imageHeight: null, imageAlt: '' }
}

function emptyForm(): Form {
  return {
    slug: '', title: '', summary: '', metaDescription: '', date: new Date().toISOString().slice(0, 10),
    heroImageId: null, heroImageWidth: null, heroImageHeight: null, sourceVersions: '', items: [emptyItem()],
  }
}

function toForm(n: ReleaseNoteDto): Form {
  return {
    slug: n.slug, title: n.title, summary: n.summary ?? '', metaDescription: n.metaDescription ?? '', date: n.date,
    heroImageId: n.heroImageId, heroImageWidth: n.heroImageWidth, heroImageHeight: n.heroImageHeight,
    sourceVersions: n.sourceVersions ?? '',
    items: n.items.map(i => ({
      key: newKey(), id: i.id, kind: i.kind, heading: i.heading, body: i.body ?? '',
      imageId: i.imageId, imageWidth: i.imageWidth, imageHeight: i.imageHeight, imageAlt: i.imageAlt ?? '',
    })),
  }
}

function toRequest(f: Form): ReleaseNoteSaveRequest {
  return {
    slug: f.slug.trim() || null,
    title: f.title.trim(),
    summary: f.summary.trim() || null,
    metaDescription: f.metaDescription.trim() || null,
    date: f.date,
    heroImageId: f.heroImageId,
    sourceVersions: f.sourceVersions.trim() || null,
    items: f.items.map(i => ({
      id: i.id, kind: i.kind, heading: i.heading.trim(), body: i.body.trim() || null,
      imageId: i.imageId, imageAlt: i.imageId ? (i.imageAlt.trim() || null) : null,
    })),
  }
}

// ─── Image picker ────────────────────────────────────────────────────────────

function ImagePicker({ imageId, onPicked, onRemove, label }: {
  imageId: string | null
  onPicked: (img: { id: string; width: number; height: number }) => void
  onRemove: () => void
  label: string
}) {
  const input = useRef<HTMLInputElement>(null)
  const upload = useMutation({
    mutationFn: (file: File) => superadminApi.uploadReleaseImage(file).then(r => r.data),
    onSuccess: onPicked,
  })

  return (
    <div className="space-y-2">
      {imageId ? (
        <div className="relative inline-block">
          <img src={releaseImageUrl(imageId)} alt="" className="max-h-44 rounded-lg border border-gray-200" />
          <button
            type="button" onClick={onRemove} title="Remove image"
            className="absolute -top-2 -right-2 h-6 w-6 rounded-full bg-white border border-gray-300 text-gray-500 hover:text-red-600 flex items-center justify-center cursor-pointer shadow-sm"
          >
            <X size={12} />
          </button>
        </div>
      ) : (
        <Button type="button" variant="outline" size="sm" disabled={upload.isPending} onClick={() => input.current?.click()}>
          <ImagePlus size={14} className="mr-1.5" /> {upload.isPending ? 'Uploading…' : label}
        </Button>
      )}
      <input
        ref={input} type="file" accept="image/png,image/jpeg,image/webp" className="hidden"
        onChange={e => { const f = e.target.files?.[0]; if (f) upload.mutate(f); e.target.value = '' }}
      />
      {upload.isError && <p className="text-xs text-red-600">{errorMessage(upload.error)}</p>}
    </div>
  )
}

// ─── Preview (approximates the public page) ──────────────────────────────────

const fmtDate = (d: string) =>
  d ? new Date(d + 'T00:00:00').toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' }) : ''

function Preview({ form }: { form: Form }) {
  return (
    <article className="bg-white border border-gray-200 rounded-xl p-6 sm:p-8 space-y-6 max-w-2xl">
      <header className="space-y-2">
        <p className="text-sm text-gray-500">{fmtDate(form.date)}</p>
        <h2 className="text-2xl font-bold text-gray-900">{form.title || 'Untitled release'}</h2>
        {form.summary && <p className="text-gray-600">{form.summary}</p>}
        {form.heroImageId && <img src={releaseImageUrl(form.heroImageId)} alt="" className="rounded-lg border border-gray-200 w-full" />}
      </header>
      {form.items.filter(i => i.heading.trim()).map(i => (
        <section key={i.key} className="space-y-2">
          <div className="flex items-center gap-2">
            <span className={`text-xs font-medium border rounded-full px-2 py-0.5 ${KIND_PILL[i.kind]}`}>{i.kind}</span>
            <h3 className="text-lg font-semibold text-gray-900">{i.heading}</h3>
          </div>
          {i.body.split(/\n\s*\n/).filter(p => p.trim()).map((p, idx) => <p key={idx} className="text-gray-600 whitespace-pre-wrap">{p}</p>)}
          {i.imageId && <img src={releaseImageUrl(i.imageId)} alt={i.imageAlt} className="rounded-lg border border-gray-200 w-full" />}
        </section>
      ))}
    </article>
  )
}

// ─── Page ────────────────────────────────────────────────────────────────────

export default function ReleaseNoteEditorPage() {
  const { id = 'new' } = useParams<{ id: string }>()
  const isNew = id === 'new'
  const navigate = useNavigate()
  const qc = useQueryClient()

  const [form, setForm] = useState<Form>(emptyForm)
  const [saved, setSaved] = useState<string>(() => JSON.stringify(toRequest(emptyForm())))
  const [note, setNote] = useState<ReleaseNoteDto | null>(null)
  const [showPreview, setShowPreview] = useState(false)
  const [message, setMessage] = useState<string | null>(null)

  const { data: loaded, isLoading } = useQuery({
    queryKey: ['superadmin-release-note', id],
    queryFn: () => superadminApi.getReleaseNote(id).then(r => r.data),
    enabled: !isNew,
  })
  const { data: settings } = useQuery({
    queryKey: ['superadmin-release-note-settings'],
    queryFn: () => superadminApi.getReleaseNoteSettings().then(r => r.data),
  })

  const adopt = (n: ReleaseNoteDto) => {
    const f = toForm(n)
    setNote(n)
    setForm(f)
    setSaved(JSON.stringify(toRequest(f)))
  }

  // Load once per note. Later changes come back from the save/publish responses, so don't clobber edits on refetch.
  useEffect(() => { if (loaded && note?.id !== loaded.id) adopt(loaded) }, [loaded]) // eslint-disable-line react-hooks/exhaustive-deps

  const afterChange = (n: ReleaseNoteDto) => {
    adopt(n)
    qc.setQueryData(['superadmin-release-note', n.id], n)
    qc.invalidateQueries({ queryKey: ['superadmin-release-notes'] })
  }

  const dirty = JSON.stringify(toRequest(form)) !== saved
  const published = note?.status === 'Published'
  const set = <K extends keyof Form>(key: K, value: Form[K]) => setForm(f => ({ ...f, [key]: value }))
  const setItem = (key: string, patch: Partial<ItemForm>) =>
    setForm(f => ({ ...f, items: f.items.map(i => i.key === key ? { ...i, ...patch } : i) }))
  const moveItem = (index: number, dir: -1 | 1) => setForm(f => {
    const items = [...f.items]
    const target = index + dir
    if (target < 0 || target >= items.length) return f
    ;[items[index], items[target]] = [items[target], items[index]]
    return { ...f, items }
  })

  const rebuildHint = settings?.rebuildConfigured
    ? 'The public site will update after it rebuilds (usually a minute or two).'
    : 'No rebuild hook is set here, so the public site only updates when its repo is next pushed or rebuilt.'

  const save = useMutation({
    mutationFn: () => isNew
      ? superadminApi.createReleaseNote(toRequest(form)).then(r => r.data)
      : superadminApi.updateReleaseNote(id, toRequest(form)).then(r => r.data),
    onSuccess: n => {
      afterChange(n)
      setMessage(n.status === 'Published' ? `Saved. ${rebuildHint}` : 'Saved.')
      if (isNew) navigate(`/superadmin/release-notes/${n.id}`, { replace: true })
    },
    onError: e => setMessage(errorMessage(e)),
  })

  const setPublished = useMutation({
    mutationFn: (publish: boolean) => (publish ? superadminApi.publishReleaseNote(id) : superadminApi.unpublishReleaseNote(id)).then(r => r.data),
    onSuccess: n => { afterChange(n); setMessage(`${n.status === 'Published' ? 'Published' : 'Unpublished'}. ${rebuildHint}`) },
    onError: e => setMessage(errorMessage(e)),
  })

  const remove = useMutation({
    mutationFn: () => superadminApi.deleteReleaseNote(id),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['superadmin-release-notes'] }); navigate('/superadmin/release-notes') },
    onError: e => setMessage(errorMessage(e)),
  })

  if (!isNew && isLoading) return <p className="text-sm text-gray-400">Loading…</p>

  const publicUrl = note && settings ? `${settings.publicBaseUrl}/whats-new/${note.slug}/` : null
  const busy = save.isPending || setPublished.isPending || remove.isPending

  return (
    <div className="space-y-6 max-w-3xl">
      <div>
        <Link to="/superadmin/release-notes" className="text-sm text-gray-500 hover:text-gray-700 inline-flex items-center gap-1">
          <ArrowLeft size={14} /> Release notes
        </Link>
        <div className="flex flex-wrap items-center justify-between gap-3 mt-2">
          <h1 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
            {isNew ? 'New release note' : 'Edit release note'}
            {note && (
              <span className={`text-xs font-medium border rounded-full px-2 py-0.5 ${published ? 'bg-green-50 text-green-700 border-green-200' : 'bg-slate-100 text-slate-600 border-slate-200'}`}>
                {note.status}
              </span>
            )}
          </h1>
          <div className="flex flex-wrap items-center gap-2">
            {published && publicUrl && (
              <a href={publicUrl} target="_blank" rel="noreferrer" className="text-sm text-indigo-600 hover:text-indigo-800 inline-flex items-center gap-1">
                <ExternalLink size={13} /> View public page
              </a>
            )}
            <Button variant="outline" size="sm" onClick={() => setShowPreview(v => !v)}>
              {showPreview ? <EyeOff size={14} className="mr-1.5" /> : <Eye size={14} className="mr-1.5" />}
              {showPreview ? 'Hide preview' : 'Preview'}
            </Button>
          </div>
        </div>
        {form.sourceVersions && (
          <p className="text-xs text-gray-400 mt-1">Drafted from internal v{form.sourceVersions.replace(/, /g, ', v')} — internal only, never shown publicly.</p>
        )}
      </div>

      {showPreview && <Preview form={form} />}

      {/* Details */}
      <div className="bg-white border border-gray-200 rounded-xl p-5 space-y-4">
        <div className="space-y-1.5">
          <Label htmlFor="title">Title *</Label>
          <Input id="title" value={form.title} maxLength={200} onChange={e => set('title', e.target.value)} placeholder="e.g. Faster invoicing and a calendar for trainers" />
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label htmlFor="date">Date shown publicly *</Label>
            <Input id="date" type="date" value={form.date} onChange={e => set('date', e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="slug">Page address</Label>
            <Input
              id="slug" value={form.slug} disabled={published} maxLength={120}
              onChange={e => set('slug', e.target.value)}
              placeholder={isNew ? 'Made from the title' : ''}
            />
            <p className="text-xs text-gray-400">
              {published ? 'Fixed once published, so links never break.' : <>/whats-new/<strong>{form.slug || '…'}</strong>/</>}
            </p>
          </div>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="summary">Summary *</Label>
          <textarea
            id="summary" value={form.summary} maxLength={600} rows={3}
            onChange={e => set('summary', e.target.value)}
            placeholder="One or two plain-English sentences — shown on the index and used when sharing."
            className="flex w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
          />
          <p className="text-xs text-gray-400 text-right">{form.summary.length}/600</p>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="meta">Search description (optional)</Label>
          <Input id="meta" value={form.metaDescription} maxLength={200} onChange={e => set('metaDescription', e.target.value)} placeholder="Defaults to the summary. About 155 characters shows in full on Google." />
          <p className="text-xs text-gray-400 text-right">{form.metaDescription.length}/200</p>
        </div>

        <div className="space-y-1.5">
          <Label>Cover image (optional)</Label>
          <p className="text-xs text-gray-400">Used on the index and when the page is shared.</p>
          <ImagePicker
            imageId={form.heroImageId} label="Add cover image"
            onPicked={img => setForm(f => ({ ...f, heroImageId: img.id, heroImageWidth: img.width, heroImageHeight: img.height }))}
            onRemove={() => setForm(f => ({ ...f, heroImageId: null, heroImageWidth: null, heroImageHeight: null }))}
          />
        </div>
      </div>

      {/* Items */}
      <div className="space-y-3">
        <h2 className="text-sm font-semibold text-gray-900">What's in this release</h2>
        {form.items.map((item, index) => (
          <div key={item.key} className="bg-white border border-gray-200 rounded-xl p-5 space-y-3">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="flex gap-1 rounded-lg bg-gray-100 p-1">
                {KINDS.map(k => (
                  <button
                    key={k} type="button" onClick={() => setItem(item.key, { kind: k })}
                    className={`px-3 py-1 text-xs rounded-md transition-colors cursor-pointer ${item.kind === k ? 'bg-white shadow-sm font-medium text-gray-900' : 'text-gray-500 hover:text-gray-700'}`}
                  >
                    {k}
                  </button>
                ))}
              </div>
              <div className="flex items-center gap-1">
                <button type="button" disabled={index === 0} onClick={() => moveItem(index, -1)} title="Move up" className="p-1.5 rounded text-gray-400 hover:text-gray-700 hover:bg-gray-100 disabled:opacity-30 cursor-pointer"><ArrowUp size={14} /></button>
                <button type="button" disabled={index === form.items.length - 1} onClick={() => moveItem(index, 1)} title="Move down" className="p-1.5 rounded text-gray-400 hover:text-gray-700 hover:bg-gray-100 disabled:opacity-30 cursor-pointer"><ArrowDown size={14} /></button>
                <button
                  type="button" title="Remove item" className="p-1.5 rounded text-gray-400 hover:text-red-600 hover:bg-red-50 cursor-pointer"
                  onClick={() => setForm(f => ({ ...f, items: f.items.filter(i => i.key !== item.key) }))}
                ><Trash2 size={14} /></button>
              </div>
            </div>

            <Input value={item.heading} maxLength={200} onChange={e => setItem(item.key, { heading: e.target.value })} placeholder="Heading, e.g. Bundle several bookings onto one invoice" />
            <textarea
              value={item.body} maxLength={4000} rows={4}
              onChange={e => setItem(item.key, { body: e.target.value })}
              placeholder="A few plain-English sentences. Leave a blank line between paragraphs."
              className="flex w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
            />

            <div className="space-y-2">
              <ImagePicker
                imageId={item.imageId} label="Add screenshot"
                onPicked={img => setItem(item.key, { imageId: img.id, imageWidth: img.width, imageHeight: img.height })}
                onRemove={() => setItem(item.key, { imageId: null, imageWidth: null, imageHeight: null, imageAlt: '' })}
              />
              {item.imageId && (
                <div className="space-y-1">
                  <Input value={item.imageAlt} maxLength={200} onChange={e => setItem(item.key, { imageAlt: e.target.value })} placeholder="Describe the screenshot (required) — e.g. The new invoice screen listing three bookings" />
                  {!item.imageAlt.trim() && <p className="text-xs text-amber-600">Needed before publishing — it's read aloud by screen readers and helps search.</p>}
                </div>
              )}
            </div>
          </div>
        ))}

        <Button type="button" variant="outline" size="sm" disabled={form.items.length >= MAX_ITEMS} onClick={() => setForm(f => ({ ...f, items: [...f.items, emptyItem()] }))}>
          <Plus size={14} className="mr-1.5" /> Add item
        </Button>
      </div>

      {/* Actions */}
      <div className="sticky bottom-0 -mx-1 bg-white/95 backdrop-blur border border-gray-200 rounded-xl px-5 py-3 space-y-2 shadow-sm">
        {message && <p className="text-sm text-gray-600">{message}</p>}
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            {!isNew && !published && (
              <Button variant="outline" size="sm" className="text-red-600 hover:text-red-700 hover:border-red-200" disabled={busy}
                onClick={() => { if (confirm('Delete this draft release note? This cannot be undone.')) remove.mutate() }}>
                <Trash2 size={14} className="mr-1.5" /> Delete
              </Button>
            )}
            {dirty && <span className="text-xs text-amber-600">Unsaved changes</span>}
          </div>
          <div className="flex items-center gap-2">
            {!isNew && (published ? (
              <Button variant="outline" size="sm" disabled={busy || dirty} title={dirty ? 'Save your changes first' : undefined}
                onClick={() => { if (confirm('Unpublish this release note? It will disappear from the public site.')) setPublished.mutate(false) }}>
                Unpublish
              </Button>
            ) : (
              <Button variant="outline" size="sm" disabled={busy || dirty} title={dirty ? 'Save your changes first' : undefined}
                onClick={() => setPublished.mutate(true)}>
                {setPublished.isPending ? 'Publishing…' : 'Publish'}
              </Button>
            ))}
            <Button size="sm" disabled={busy || !dirty || !form.title.trim()} onClick={() => { setMessage(null); save.mutate() }}>
              {save.isPending ? 'Saving…' : isNew ? 'Create draft' : 'Save'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}
